import { Types } from "../actionTypes";
import { createSelector } from "@reduxjs/toolkit";
import { concat, orderBy, remove, slice, sample } from "lodash";
import { ApiStatus, categoryKeys } from "@Src/constants";
import * as defaults from "@Src/components/Header/MegaCatMenu/Dropdown/content/data/defaults";

// Campaigns running on the home page and dropdown menu
export const initialState = {
  mainBanners: {
    api: {
      status: null,
      error: null,
    },
    stores: [],
  },
  featuredDeals: {
    api: {
      status: null,
      error: null,
    },
    stores: [],
  },
  favoritesNearby: {
    api: {
      status: null,
      error: null,
    },
    stores: [],
  },
  topOnlineDeals: {
    api: {
      status: null,
      error: null,
    },
    stores: [],
  },
  popularBrands: {
    api: {
      status: null,
      error: null,
    },
    stores: [],
  },
  // Related to items you've viewed on the home page
  recommendations: {
    api: {
      status: null,
      error: null,
    },
    stores: [],
  },
  hotDeals: {
    api: {
      status: null,
      error: null,
    },
    hot: null,
    popular: null,
    ourPick: null,
    all: [],
  },
  ourPicks: {
    [categoryKeys.EAT]: null,
    [categoryKeys.PLAY]: null,
    [categoryKeys.SHOP]: null,
    [categoryKeys.THEMEPARKS]: null,
    api: {
      status: null,
      error: null,
    },
  },
};

function campaign(state = initialState, action) {
  switch (action.type) {
    case Types.FETCH_BANNER_DEALS:
      return {
        ...state,
        mainBanners: {
          ...state.mainBanners,
          api: {
            status: ApiStatus.LOADING,
            error: null,
          },
        },
      };
    case Types.FETCH_BANNER_DEALS_SUCCESS:
      return {
        ...state,
        mainBanners: {
          ...state.mainBanners,
          api: {
            status: ApiStatus.SUCCESS,
            error: null,
          },
          stores: action.response,
        },
      };
    case Types.FETCH_BANNER_DEALS_FAILURE:
      return {
        ...state,
        mainBanners: {
          ...state.mainBanners,
          api: {
            status: ApiStatus.FAILURE,
            error: action.error,
          },
        },
      };
    case Types.FETCH_HOT_DEALS:
      return {
        ...state,
        hotDeals: {
          ...state.hotDeals,
          api: {
            status: ApiStatus.LOADING,
            error: null,
          },
        },
      };
    case Types.FETCH_HOT_DEALS_SUCCESS:
      return {
        ...state,
        hotDeals: {
          ...state.hotDeals,
          api: {
            status: ApiStatus.SUCCESS,
            error: null,
          },
          ...mapHotDealsContent(action.response.stores, state),
          all: action.response.stores,
        },
      };
    case Types.FETCH_HOT_DEALS_FAILURE:
      return {
        ...state,
        hotDeals: {
          ...state.hotDeals,
          api: {
            status: ApiStatus.FAILURE,
            error: action.error,
          },
        },
      };
    case Types.FETCH_OUR_PICKS:
      return {
        ...state,
        ourPicks: {
          ...state.ourPicks,
          api: {
            status: ApiStatus.LOADING,
            error: null,
          },
        },
      };
    case Types.FETCH_OUR_PICKS_SUCCESS:
      return {
        ...state,
        ourPicks: {
          ...state.ourPicks,
          [categoryKeys.EAT]: mapOurPick(action.response.eatPlayShop, categoryKeys.EAT) || null,
          [categoryKeys.PLAY]: mapOurPick(action.response.eatPlayShop, categoryKeys.PLAY) || null,
          [categoryKeys.SHOP]: mapOurPick(action.response.eatPlayShop, categoryKeys.SHOP) || null,
          [categoryKeys.THEMEPARKS]: mapOurPick(action.response.themeParks, null, true) || null,
          api: {
            status: ApiStatus.SUCCESS,
            error: null,
          },
        },
      };
    case Types.FETCH_OUR_PICKS_FAILURE:
      return {
        ...state,
        ourPicks: {
          ...state.ourPicks,
          api: {
            status: ApiStatus.FAILURE,
            error: action.error,
          },
        },
      };
    case Types.FETCH_FEATURED_DEALS:
      return {
        ...state,
        featuredDeals: {
          ...state.featuredDeals,
          api: {
            status: ApiStatus.LOADING,
            error: null,
          },
        },
      };
    case Types.FETCH_FEATURED_DEALS_SUCCESS:
      return {
        ...state,
        featuredDeals: {
          ...state.featuredDeals,
          api: {
            status: ApiStatus.SUCCESS,
            error: null,
          },
          stores: action.response,
        },
      };
    case Types.FETCH_FEATURED_DEALS_FAILURE:
      return {
        ...state,
        featuredDeals: {
          ...state.featuredDeals,
          api: {
            status: ApiStatus.FAILURE,
            error: action.error,
          },
        },
      };
    case Types.FETCH_FAVORITES_NEARBY:
      return {
        ...state,
        favoritesNearby: {
          ...state.favoritesNearby,
          api: {
            status: ApiStatus.LOADING,
            error: null,
          },
        },
      };
    case Types.FETCH_FAVORITES_NEARBY_SUCCESS:
      let favouritesNearbyStores = mapStores(action.response.stores);

      return {
        ...state,
        favoritesNearby: {
          ...state.favoritesNearby,
          api: {
            status: ApiStatus.SUCCESS,
            error: null,
          },
          stores: favouritesNearbyStores,
        },
      };
    case Types.FETCH_FAVORITES_NEARBY_FAILURE:
      return {
        ...state,
        favoritesNearby: {
          ...state.favoritesNearby,
          api: {
            status: ApiStatus.FAILURE,
            error: action.error,
          },
        },
      };
    case Types.FETCH_POPULAR_BRANDS:
      return {
        ...state,
        popularBrands: {
          ...state.popularBrands,
          api: {
            status: ApiStatus.LOADING,
            error: null,
          },
        },
      };
    case Types.FETCH_POPULAR_BRANDS_SUCCESS:
      return {
        ...state,
        popularBrands: {
          ...state.popularBrands,
          api: {
            status: ApiStatus.SUCCESS,
            error: null,
          },
          stores: action.response.stores,
        },
      };
    case Types.FETCH_POPULAR_BRANDS_FAILURE:
      return {
        ...state,
        popularBrands: {
          ...state.popularBrands,
          api: {
            status: ApiStatus.FAILURE,
            error: action.error,
          },
        },
      };
    case Types.FETCH_RECOMMENDATIONS:
      return {
        ...state,
        recommendations: {
          ...state.recommendations,
          api: {
            status: ApiStatus.LOADING,
            error: null,
          },
        },
      };
    case Types.FETCH_RECOMMENDATIONS_SUCCESS:
      return {
        ...state,
        recommendations: {
          ...state.recommendations,
          api: {
            status: ApiStatus.SUCCESS,
            error: null,
          },
          stores: action.response,
        },
      };
    case Types.FETCH_RECOMMENDATIONS_FAILURE:
      return {
        ...state,
        recommendations: {
          ...state.recommendations,
          api: {
            status: ApiStatus.FAILURE,
            error: action.error,
          },
        },
      };
    case Types.FETCH_TOP_ONLINE_DEALS:
      return {
        ...state,
        topOnlineDeals: {
          ...state.topOnlineDeals,
          api: {
            status: ApiStatus.LOADING,
            error: null,
          },
        },
      };
    case Types.FETCH_TOP_ONLINE_DEALS_SUCCESS:
      return {
        ...state,
        topOnlineDeals: {
          ...state.topOnlineDeals,
          api: {
            status: ApiStatus.SUCCESS,
            error: null,
          },
          stores: action.response,
        },
      };
    case Types.FETCH_TOP_ONLINE_DEALS_FAILURE:
      return {
        ...state,
        topOnlineDeals: {
          ...state.topOnlineDeals,
          api: {
            status: ApiStatus.FAILURE,
            error: action.error,
          },
        },
      };
    default:
      return state;
  }
}

export const getBannerDeals = (state) => state.mainBanners.stores;
export const getBannersLoading = (state) => {
  if (state.mainBanners.api.status === ApiStatus.LOADING && !state.mainBanners.stores.length) {
    return true;
  }
  return false;
};
export const getFeaturedDeals = (state) => state.featuredDeals.stores;
export const getFavoritesNearby = (state) => state.favoritesNearby.stores;
export const getTopOnlineDeals = (state) => state.topOnlineDeals.stores;
export const getPopularBrands = (state) => state.popularBrands.stores;
export const getAllRecommendations = (state) => state.recommendations.stores;
export const getHotDeals = createSelector([(state) => state.hotDeals], (hotDeals) => {
  return {
    hot: hotDeals.hot,
    popular: hotDeals.popular,
    ourPick: hotDeals.ourPick,
    all: hotDeals.all,
  };
});
export const getHotDealsApiStatus = (state) => state.hotDeals.api.status;
export const getOurPicks = createSelector([(state) => state.ourPicks], (ourPicks) => {
  const eat = ourPicks[categoryKeys.EAT];
  const play = ourPicks[categoryKeys.PLAY];
  const shop = ourPicks[categoryKeys.SHOP];
  const parks = ourPicks[categoryKeys.THEMEPARKS];

  return {
    [categoryKeys.EAT]: eat || defaults.eatDefaultPick(),
    [categoryKeys.PLAY]: play || defaults.playDefaultPick(),
    [categoryKeys.SHOP]: shop || defaults.shopDefaultPick(),
    [categoryKeys.THEMEPARKS]: parks || defaults.themeParksDefaultPick(),
  };
});
export const getOurPicksApiStatus = (state) => state.ourPicks.api.status;
export const getFavoritesInitLoading = (state) => {
  if (
    state.favoritesNearby.api.status === ApiStatus.LOADING &&
    !state.favoritesNearby.stores.length
  ) {
    return true;
  }
  return false;
};
export const getTopOnlineDealsInitLoading = (state) => {
  return (
    state.topOnlineDeals.api.status === ApiStatus.LOADING && !state.topOnlineDeals.stores.length
  );
};
export const getPopularInitLoading = (state) => {
  if (state.popularBrands.api.status === ApiStatus.LOADING && !state.popularBrands.stores?.length) {
    return true;
  }
  return false;
};
export const getFeaturedInitLoading = (state) => {
  if (state.featuredDeals.api.status === ApiStatus.LOADING && !state.featuredDeals.stores.length) {
    return true;
  }
  return false;
};
export const getRecommendationsInitLoading = (state) => {
  if (
    state.recommendations.api.status === ApiStatus.LOADING &&
    !state.recommendations.stores.length
  ) {
    return true;
  }
  return false;
};

export function mapFromRecommendations(recommendations) {
  return recommendations.map((item) => {
    return {
      store_key: item.store_key,
      name: item.brand_name,
      description: item.offer_text,
      imageUrl: item.image_url,
    };
  });
}

export function mapHotDealsContent(allDeals, state) {
  if (allDeals) {
    // Get our pick first
    const ourPick = mapOurPick(allDeals);
    // Filter out ourPick from remaining deals
    const remainingDeals = allDeals.filter((deal) =>
      ourPick ? deal.store_key !== ourPick.store_key : deal,
    );
    return {
      hot: remainingDeals.slice(0, 10),
      popular: remainingDeals.slice(10, 14),
      ourPick: ourPick || defaults.hotDealsDefaultPick(),
    };
  }
  return {
    hot: null,
    popular: null,
    ourPick: null,
  };
}

export function mapOurPick(brands, categoryKey, isThemePark = false) {
  let brandsToFilter = brands || [];
  if (categoryKey) {
    brandsToFilter = brands?.filter((brand) => {
      return brand?.categories?.includes(categoryKey);
    });
  }

  const dealsWithAnImage = brandsToFilter?.filter((brand) => brand?.brand_photos?.PHOTO?.length);
  const dynamicOurPick = sample(dealsWithAnImage);

  let ourPick = dynamicOurPick;
  if (!ourPick) return null;

  return {
    mainText: isThemePark ? `Save on tickets` : `Save at ${ourPick.name}`,
    subText: isThemePark ? ourPick.name : `View Deals`,
    img: ourPick.brand_photos?.PHOTO[0],
    ...ourPick,
  };
}

export function mapStores(data) {
  let stores = data;
  // remove stores in hotel or car-rental categories
  remove(stores, (store) => {
    const { categories } = store;
    const { HOTEL, CAR_RENTAL } = categoryKeys;
    return categories.includes(HOTEL) || categories.includes(CAR_RENTAL);
  });

  // remove stores with zero total_redeems
  let zeroRedeems = remove(stores, (store) => {
    return store.total_redeems <= 0;
  });

  // sort remaining stores by total_redeems, most first.
  stores = orderBy(stores, "total_redeems", "desc");

  // join remaining stores with zero-redeem stores at the end,
  // then truncate list down to 20 - don't want to display more than that.
  stores = slice(concat(stores, zeroRedeems), 0, 20);

  return stores;
}

export default campaign;
